import React, { Suspense, useRef } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Environment } from "@react-three/drei";

const h = React.createElement;

/**
 * 3D pipe clamp viewer (Three.js).
 * IMPORTANT: We use React.createElement (no JSX) for all R3F intrinsic elements
 * to avoid the visual-edits babel plugin injecting x-line-number attributes,
 * which crashes R3F's applyProps when setting them on Three.js objects.
 */
function PipeAndClamp({ diameter, stripWidth, stripThickness, materialColor, rubberLining, rubberColor }) {
  const groupRef = useRef();
  const r = Math.max(0.3, diameter / 60);
  const sw = Math.max(0.08, stripWidth / 250);
  const st = Math.max(0.04, stripThickness / 220);

  useFrame(({ clock }) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = Math.sin(clock.elapsedTime * 0.3) * 0.4 + 0.5;
    }
  });

  return h(
    "group",
    { ref: groupRef },
    // Pipe
    h(
      "mesh",
      { castShadow: true, receiveShadow: true },
      h("cylinderGeometry", { args: [r, r, 3, 64, 1, false] }),
      h("meshStandardMaterial", { color: "#9ca3af", metalness: 0.9, roughness: 0.35 })
    ),
    // Rubber liner
    rubberLining
      ? h(
          "mesh",
          null,
          h("torusGeometry", { args: [r + 0.02, 0.02, 16, 96] }),
          h("meshStandardMaterial", { color: rubberColor || "#1a1a1a", metalness: 0.0, roughness: 0.9 })
        )
      : null,
    // Clamp torus
    h(
      "mesh",
      { castShadow: true },
      h("torusGeometry", { args: [r + st * 0.5 + 0.02, st * 0.5, 24, 96] }),
      h("meshStandardMaterial", { color: materialColor, metalness: 1.0, roughness: 0.18 })
    ),
    // Mounting ears
    h(
      "mesh",
      { position: [0, r + st + 0.18, 0], castShadow: true },
      h("boxGeometry", { args: [st * 6, 0.25, sw] }),
      h("meshStandardMaterial", { color: materialColor, metalness: 1.0, roughness: 0.2 })
    ),
    // Threaded rod
    h(
      "mesh",
      { position: [0, r + st + 0.75, 0], castShadow: true },
      h("cylinderGeometry", { args: [0.05, 0.05, 1.2, 16] }),
      h("meshStandardMaterial", { color: "#d4d4d8", metalness: 1.0, roughness: 0.3 })
    )
  );
}

class ViewerErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(err, info) {
    // eslint-disable-next-line no-console
    console.error("3D viewer error:", err, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-full w-full items-center justify-center bg-white p-8">
          <div className="text-center">
            <div className="label-uppercase mb-2">3D preview niet beschikbaar</div>
            <div className="font-display text-lg text-zinc-500">
              Je configuratie wordt nog berekend.
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function Scene({ diameter, stripWidth, stripThickness, materialColor, rubberLining, rubberColor }) {
  return h(
    React.Fragment,
    null,
    h("color", { attach: "background", args: ["#f5f7f5"] }),
    h("fog", { attach: "fog", args: ["#f5f7f5", 12, 25] }),
    h("ambientLight", { intensity: 0.6 }),
    h("directionalLight", { position: [5, 5, 5], intensity: 0.9, castShadow: true }),
    h("directionalLight", { position: [-5, 3, -2], intensity: 0.4, color: "#9DBAA1" }),
    h(
      Suspense,
      { fallback: null },
      h(PipeAndClamp, {
        diameter,
        stripWidth,
        stripThickness,
        materialColor,
        rubberLining: rubberLining && rubberLining !== "none",
        rubberColor,
      }),
      h(Environment, { preset: "warehouse" })
    ),
      h(OrbitControls, {
      enableZoom: true,
      enablePan: false,
      minDistance: 3.5,
      maxDistance: 12,
      autoRotate: false,
    })
  );
}

export default function ClampViewer3D({
  diameter = 33.7,
  stripWidth = 25,
  stripThickness = 3,
  material = "304",
  rubberLining = null,
}) {
  const materialColor =
    material === "316" ? "#cbd5d8"
      : material === "duplex" ? "#aab4bd"
      : material === "galvanized" ? "#9a958e"
      : "#d4d4d8";

  const rubberColor =
    rubberLining === "epdm" ? "#0a0a0a" :
    rubberLining === "silicone" ? "#7f1d1d" :
    rubberLining === "high_temp" ? "#451a03" : null;

  return (
    <div className="relative h-full w-full" data-testid="clamp-3d-viewer">
      <ViewerErrorBoundary>
        {h(
          Canvas,
          {
            camera: { position: [4.5, 2.2, 5.2], fov: 38 },
            dpr: [1, 1.5],
            gl: { antialias: true, alpha: true },
          },
          h(Scene, {
            diameter,
            stripWidth,
            stripThickness,
            materialColor,
            rubberLining,
            rubberColor,
          })
        )}
      </ViewerErrorBoundary>

      <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: "linear-gradient(rgba(157,186,161,0.12) 1px, transparent 1px), linear-gradient(90deg, rgba(157,186,161,0.12) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
      <div className="pointer-events-none absolute left-4 top-4 rounded border border-nibro-sage/40 bg-white/90 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-nibro-sage-dark backdrop-blur">
        3D Preview · Sleep om te draaien
      </div>
      <div className="pointer-events-none absolute right-4 top-4 rounded border border-nibro-sage/40 bg-white/90 px-3 py-1.5 font-mono text-[11px] font-bold text-nibro-sage-dark backdrop-blur">
        Ø {diameter.toFixed(1)} mm
      </div>
    </div>
  );
}
